package com.partejar.app.feature.relacionamentos.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarShapes
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.relacionamentos.data.RelacionamentoStage

internal fun relationshipInitials(name: String): String = name
    .trim()
    .split(Regex("\\s+"))
    .filter(String::isNotBlank)
    .let { parts ->
        listOfNotNull(
            parts.firstOrNull()?.firstOrNull(),
            parts.drop(1).lastOrNull()?.firstOrNull()
        )
    }
    .joinToString("")
    .uppercase()

internal fun journeyDescription(stage: RelacionamentoStage): String =
    journeyPosition(stage)?.let { "${stageShortLabel(stage)} · etapa ${it + 1} de ${relationshipJourney.size}" }
        ?: stageShortLabel(stage)

@Composable
internal fun RelationshipAvatar(name: String, size: Dp, modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier.size(size),
        shape = PartejarShapes.pill,
        color = MaterialTheme.colorScheme.tertiaryContainer,
        contentColor = MaterialTheme.colorScheme.tertiary
    ) {
        Box(contentAlignment = Alignment.Center) {
            Text(
                relationshipInitials(name),
                style = MaterialTheme.typography.titleSmall,
                modifier = Modifier.clearAndSetSemantics {}
            )
        }
    }
}

/** Compact progress used on list cards. */
@Composable
internal fun RelationshipJourneyBar(stage: RelacionamentoStage, modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    val position = journeyPosition(stage)
    Row(
        modifier = modifier.semantics { contentDescription = journeyDescription(stage) },
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.width(spacing.space64 + spacing.space8),
            horizontalArrangement = Arrangement.spacedBy(spacing.space4)
        ) {
            relationshipJourney.indices.forEach { index ->
                Box(
                    Modifier
                        .weight(1f)
                        .height(spacing.space4)
                        .clip(PartejarShapes.pill)
                        .background(
                            if (position != null && index <= position) MaterialTheme.colorScheme.primary
                            else MaterialTheme.colorScheme.outline
                        )
                )
            }
        }
        Text(
            journeyDescription(stage),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.clearAndSetSemantics {}
        )
    }
}

/** Numbered stepper used at the top of the detail. */
@Composable
internal fun RelationshipJourneyStepper(stage: RelacionamentoStage, modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val position = journeyPosition(stage)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .semantics { contentDescription = "Etapa atual: ${journeyDescription(stage)}" },
        horizontalArrangement = Arrangement.spacedBy(spacing.space4)
    ) {
        relationshipJourney.forEachIndexed { index, step ->
            val done = position != null && index < position
            val current = index == position
            Column(
                modifier = Modifier.weight(1f).clearAndSetSemantics {},
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(spacing.space8)
            ) {
                Surface(
                    modifier = Modifier.size(spacing.space32),
                    shape = PartejarShapes.pill,
                    color = when {
                        done -> MaterialTheme.colorScheme.primary
                        current -> MaterialTheme.colorScheme.primaryContainer
                        else -> Color.Transparent
                    },
                    contentColor = when {
                        done -> MaterialTheme.colorScheme.onPrimary
                        current -> MaterialTheme.colorScheme.onPrimaryContainer
                        else -> MaterialTheme.colorScheme.onSurfaceVariant
                    },
                    border = when {
                        done -> null
                        current -> BorderStroke(dimensions.strokeStrong, MaterialTheme.colorScheme.primary)
                        else -> BorderStroke(dimensions.strokeStrong, MaterialTheme.colorScheme.outline)
                    }
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        if (done) {
                            Icon(PartejarIcons.Feedback.success, contentDescription = null, modifier = Modifier.size(dimensions.iconSmall))
                        } else {
                            Text("${index + 1}", style = MaterialTheme.typography.labelLarge)
                        }
                    }
                }
                Text(
                    stageShortLabel(step),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = if (current) FontWeight.Bold else FontWeight.Medium,
                    color = if (current) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant,
                    textAlign = TextAlign.Center,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

internal data class RelationshipToneColors(val container: Color, val content: Color, val accent: Color)

@Composable
internal fun relationshipToneColors(tone: RelationshipHintTone): RelationshipToneColors = when (tone) {
    RelationshipHintTone.ATTENTION -> RelationshipToneColors(
        PartejarTheme.colors.attentionContainer,
        PartejarTheme.colors.onAttentionContainer,
        PartejarTheme.colors.attention
    )
    RelationshipHintTone.SCHEDULED -> RelationshipToneColors(
        PartejarTheme.colors.infoContainer,
        PartejarTheme.colors.onInfoContainer,
        PartejarTheme.colors.info
    )
    RelationshipHintTone.NEUTRAL -> RelationshipToneColors(
        MaterialTheme.colorScheme.surfaceVariant,
        MaterialTheme.colorScheme.onSurfaceVariant,
        MaterialTheme.colorScheme.onSurfaceVariant
    )
}

internal fun relationshipHintIcon(kind: RelationshipHintKind): ImageVector = when (kind) {
    RelationshipHintKind.MEETING -> PartejarIcons.Relationships.meeting
    RelationshipHintKind.FOLLOW_UP -> PartejarIcons.Clinical.schedule
    RelationshipHintKind.STALE -> PartejarIcons.Feedback.attention
    RelationshipHintKind.CONTRACT -> PartejarIcons.Relationships.contract
    RelationshipHintKind.NONE -> PartejarIcons.Actions.add
    RelationshipHintKind.CLOSED -> PartejarIcons.Relationships.lost
}

@Composable
internal fun RelationshipHintBand(hint: RelationshipHint, modifier: Modifier = Modifier) {
    val spacing = PartejarTheme.spacing
    val colors = relationshipToneColors(hint.tone)
    Row(
        modifier = modifier
            .fillMaxWidth()
            .background(colors.container)
            .padding(horizontal = spacing.space16, vertical = spacing.space12),
        horizontalArrangement = Arrangement.spacedBy(spacing.space8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(
            relationshipHintIcon(hint.kind),
            contentDescription = null,
            modifier = Modifier.size(PartejarTheme.dimensions.iconSmall),
            tint = colors.accent
        )
        Text(
            hint.text,
            style = MaterialTheme.typography.labelLarge,
            color = colors.content
        )
    }
}
